import { useSearchParams, Link } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { Notice } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";

const PAGE_SIZE = 10;

function formatDate(d?: string): string {
  if (!d) return "";
  return d.slice(0, 10).replace(/-/g, ".");
}

function isFixed(n: Notice): boolean {
  return n.fixed === 1 || n.is_fixed === 1 || n.top === 1;
}

export default function NoticeList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const { data, total, loading, error, reload } = useApi<Notice[]>(
    `notices.php?page=${page}&size=${PAGE_SIZE}`
  );
  const notices = data ?? [];
  const totalPages = Math.max(1, Math.ceil((total || notices.length) / PAGE_SIZE));

  const goPage = (p: number) => {
    if (p === 1) setSearchParams({});
    else setSearchParams({ page: String(p) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <PageBanner
        title="공지사항"
        description="클럽의 주요 소식과 안내를 확인하세요."
        breadcrumb={[{ label: "공지사항" }]}
      />

      <section className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16">
        {loading ? (
          <div className="divide-y divide-background-200 rounded-xl border border-background-200 bg-background-50">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4 md:p-5">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="공지사항을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
              >
                다시 시도
              </button>
            }
          />
        ) : notices.length === 0 ? (
          <EmptyState icon="ri-file-list-3-line" title="등록된 공지가 없습니다" />
        ) : (
          <>
            <div className="divide-y divide-background-200 rounded-xl border border-background-200 bg-background-50 overflow-hidden">
              {notices.map((n) => (
                <Link
                  key={n.no}
                  to={`/notices/${n.no}`}
                  className="flex items-center gap-4 p-4 md:p-5 hover:bg-background-100 transition-colors"
                >
                  <span
                    className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-full ${
                      isFixed(n)
                        ? "bg-secondary-500 text-primary-950"
                        : "bg-primary-100 text-primary-700"
                    }`}
                  >
                    <i
                      className={isFixed(n) ? "ri-pushpin-2-fill" : "ri-megaphone-line"}
                      aria-hidden="true"
                    ></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {isFixed(n) && (
                        <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-900">
                          공지
                        </span>
                      )}
                      <span className="truncate font-medium text-foreground-900">
                        {n.title}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-foreground-500">
                      <span>{formatDate(n.reg_date || n.date)}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line" aria-hidden="true"></i>
                        {n.hit ?? 0}
                      </span>
                    </div>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
                </Link>
              ))}
            </div>

            {/* 페이징 */}
            {totalPages > 1 && (
              <nav
                className="mt-8 flex items-center justify-center gap-1.5"
                aria-label="페이지 이동"
              >
                <button
                  type="button"
                  onClick={() => goPage(page - 1)}
                  disabled={page <= 1}
                  aria-label="이전 페이지"
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
                </button>
                {Array.from({ length: totalPages }).map((_, i) => {
                  const p = i + 1;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => goPage(p)}
                      aria-current={p === page ? "page" : undefined}
                      className={`min-w-9 h-9 px-2 flex items-center justify-center rounded-md text-sm font-medium transition-colors ${
                        p === page
                          ? "bg-primary-500 text-background-50"
                          : "border border-background-200 text-foreground-600 hover:bg-background-100"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => goPage(page + 1)}
                  disabled={page >= totalPages}
                  aria-label="다음 페이지"
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                </button>
              </nav>
            )}
          </>
        )}
      </section>
    </>
  );
}